import React, { useEffect, useState } from 'react';
import { Network, Link2, Copy, CheckCircle2, Users, DollarSign, Loader2, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

/**
 * SubAffiliatesTab — Founding Creator dashboard tab for the 2-tier
 * referral program. Mirrors Dub Ranked: opt-in once, share your recruiter
 * link, earn 1% override on any sale driven by an affiliate you recruited.
 *
 * Three sub-views inside the tab:
 *   1. Overview — opt-in CTA OR (when enrolled) hero stats: total earned,
 *      pending, downstream count, recruiter link with copy button.
 *   2. Downstream — list of recruited sub-affiliates + per-row earnings.
 *   3. Ledger — paginated override events for accounting.
 *
 * Pulls from /api/sub-affiliate/* endpoints. Polls once on mount; refetches
 * after enroll() succeeds.
 */
export default function SubAffiliatesTab() {
  const API = process.env.REACT_APP_BACKEND_URL;
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  const [status, setStatus] = useState(null);
  const [summary, setSummary] = useState(null);
  const [downstream, setDownstream] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [view, setView] = useState('overview');
  const [copied, setCopied] = useState(false);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const loadAll = async () => {
    setLoading(true);
    try {
      const statusRes = await fetch(`${API}/api/sub-affiliate/my-status`, { headers: authHeaders });
      const statusData = await statusRes.json();
      setStatus(statusData);

      if (statusData.eligible && statusData.enrolled) {
        const [sumRes, dsRes, ovRes] = await Promise.all([
          fetch(`${API}/api/sub-affiliate/summary`, { headers: authHeaders }),
          fetch(`${API}/api/sub-affiliate/downstream`, { headers: authHeaders }),
          fetch(`${API}/api/sub-affiliate/overrides`, { headers: authHeaders }),
        ]);
        setSummary(await sumRes.json());
        setDownstream((await dsRes.json()).downstream || []);
        setOverrides((await ovRes.json()).overrides || []);
      }
    } catch (e) {
      toast.error('Failed to load Sub-Affiliate data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const handleEnroll = async () => {
    setEnrolling(true);
    try {
      const res = await fetch(`${API}/api/sub-affiliate/enroll`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Enroll failed');
      toast.success("You're enrolled in the Sub-Affiliate Program.");
      await loadAll();
    } catch (e) {
      toast.error(e.message || 'Enroll failed');
    } finally {
      setEnrolling(false);
    }
  };

  const copyLink = () => {
    if (!status?.recruiter_link) return;
    navigator.clipboard.writeText(status.recruiter_link);
    setCopied(true);
    toast.success('Recruiter link copied');
    setTimeout(() => setCopied(false), 2500);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="sub-affiliates-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#00F0FF]" />
      </div>
    );
  }

  // -- Not eligible (not a Founding Creator) --
  if (!status?.eligible) {
    return (
      <div
        className="rounded-2xl border border-white/10 bg-white/[0.02] p-8 text-center"
        data-testid="sub-affiliates-ineligible"
      >
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-white/5 mb-4">
          <Lock className="w-5 h-5 text-neutral-400" />
        </div>
        <h3 className="text-xl font-bold text-white mb-2">Founding Creators only</h3>
        <p className="text-sm text-neutral-400 max-w-md mx-auto leading-relaxed">
          The Sub-Affiliate Program is reserved for Founding Creators. Apply for one of the
          remaining Founding Creator spots to unlock it.
        </p>
      </div>
    );
  }

  // -- Eligible but not enrolled --
  if (!status.enrolled) {
    return (
      <div
        className="rounded-2xl border border-[#00F0FF]/25 bg-gradient-to-br from-[#00F0FF]/[0.05] to-transparent p-8"
        data-testid="sub-affiliates-enroll"
      >
        <div className="flex items-start gap-4">
          <div className="rounded-xl bg-[#00F0FF]/15 p-3 text-[#00F0FF]">
            <Network className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <h3 className="text-2xl font-bold text-white mb-2">
              Earn from other creators' sales
            </h3>
            <p className="text-sm text-neutral-300 leading-relaxed mb-5 max-w-2xl">
              As a Founding Creator, you can recruit sub-affiliates. When someone signs up via
              your recruiter link and their referrals generate sales on theailedger, you earn a
              <b className="text-[#00F0FF]"> {status.override_percent ?? 1}% override</b> on every
              one of those sales — paid out of the platform fee, never out of their earnings.
            </p>
            <ul className="text-xs text-neutral-400 space-y-1.5 mb-6 list-disc pl-5">
              <li>Recruit once, earn passively forever</li>
              <li>No impact on your sub-affiliates' commissions — they get the same deal as anyone</li>
              <li>1% of GMV on every downstream-driven sale, automatically tracked</li>
            </ul>
            <Button
              onClick={handleEnroll}
              disabled={enrolling}
              data-testid="sub-affiliates-enroll-btn"
              className="bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90 font-bold"
            >
              {enrolling ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Enrolling…</> : 'Enroll in Sub-Affiliate Program'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // -- Enrolled --
  const fmtUSD = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

  return (
    <div className="space-y-6" data-testid="sub-affiliates-tab-enrolled">
      {/* Hero stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <StatCard
          icon={<DollarSign className="w-4 h-4" />}
          label="Total earned"
          value={fmtUSD(summary?.total_overrides_earned_cents)}
          accent
          testId="stat-total-earned"
        />
        <StatCard
          icon={<DollarSign className="w-4 h-4" />}
          label="Pending payout"
          value={fmtUSD(summary?.pending_payout_cents)}
          testId="stat-pending"
        />
        <StatCard
          icon={<Users className="w-4 h-4" />}
          label="Sub-affiliates"
          value={summary?.downstream_count ?? 0}
          testId="stat-downstream-count"
        />
        <StatCard
          icon={<Network className="w-4 h-4" />}
          label="Override events"
          value={summary?.override_events_count ?? 0}
          testId="stat-event-count"
        />
      </div>

      {/* Recruiter link */}
      <div className="rounded-xl border border-white/10 bg-neutral-900 p-5">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Link2 className="w-4 h-4 text-[#00F0FF]" />
            <h4 className="font-bold text-white text-sm">Your recruiter link</h4>
          </div>
          <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#00F0FF]">
            {status.override_percent ?? 1}% override
          </span>
        </div>
        <div className="flex items-center gap-2 bg-black/50 border border-white/10 rounded-lg px-3 py-2.5 mb-2">
          <code className="flex-1 text-xs text-neutral-300 truncate font-mono" data-testid="recruiter-link-value">
            {status.recruiter_link}
          </code>
          <Button
            size="sm"
            onClick={copyLink}
            data-testid="copy-recruiter-link-btn"
            className="bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90 px-3 h-7 text-xs whitespace-nowrap"
          >
            {copied ? <><CheckCircle2 className="w-3 h-3 mr-1" />Copied</> : <><Copy className="w-3 h-3 mr-1" />Copy</>}
          </Button>
        </div>
        <p className="text-[11px] text-neutral-500 leading-snug">
          Anyone who signs up via this link is permanently linked to you. You earn {status.override_percent ?? 1}%
          of GMV on every sale their referrals generate — for life.
        </p>
      </div>

      {/* Toggle between downstream + ledger */}
      <Tabs value={view} onValueChange={setView}>
        <TabsList className="bg-neutral-900 border border-white/10">
          <TabsTrigger value="overview" data-testid="sub-aff-view-overview">Recruits</TabsTrigger>
          <TabsTrigger value="ledger" data-testid="sub-aff-view-ledger">Override Ledger</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          {downstream.length === 0 ? (
            <EmptyHint
              title="No recruits yet"
              body="Share your link in your audience, network, Discord, or X bio. The first recruit is the hardest — momentum compounds from there."
            />
          ) : (
            <div className="space-y-2" data-testid="downstream-list">
              {downstream.map((d) => (
                <div
                  key={d.user_id}
                  className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-neutral-900 px-4 py-3"
                  data-testid={`downstream-row-${d.user_id}`}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-white truncate">{d.name || d.email}</div>
                    <div className="text-xs text-neutral-500 truncate">
                      {d.role || 'user'} · joined {d.created_at ? new Date(d.created_at).toLocaleDateString() : '—'}
                    </div>
                  </div>
                  <div className="text-right whitespace-nowrap">
                    <div className="text-sm font-bold text-[#00F0FF]">{fmtUSD(d.override_earned_cents)}</div>
                    <div className="text-[10px] text-neutral-500">{d.override_event_count || 0} events</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="ledger" className="mt-4">
          {overrides.length === 0 ? (
            <EmptyHint
              title="No overrides yet"
              body="Override events appear here when a sub-affiliate of yours drives a sale. Each row = 1% of that sale's GMV."
            />
          ) : (
            <div className="overflow-x-auto" data-testid="overrides-table">
              <table className="w-full text-sm">
                <thead className="text-xs text-neutral-500 uppercase tracking-wider">
                  <tr className="border-b border-white/10">
                    <th className="text-left py-3">Date</th>
                    <th className="text-left">Order</th>
                    <th className="text-right">Sale</th>
                    <th className="text-right">Override</th>
                    <th className="text-right">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {overrides.map((o) => (
                    <tr key={o.override_id} className="border-b border-white/5">
                      <td className="py-3 text-neutral-400 text-xs">
                        {new Date(o.created_at).toLocaleDateString()}
                      </td>
                      <td className="text-neutral-400 text-xs font-mono">
                        {(o.order_id || '').slice(-10)}
                      </td>
                      <td className="text-right text-neutral-300">{fmtUSD(o.order_subtotal_cents)}</td>
                      <td className="text-right font-bold text-[#00F0FF]">{fmtUSD(o.override_cents)}</td>
                      <td className="text-right">
                        <span className={`text-[10px] uppercase font-bold ${o.status === 'paid_out' ? 'text-emerald-400' : 'text-amber-400'}`}>
                          {o.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

const StatCard = ({ icon, label, value, accent, testId }) => (
  <div
    className={`rounded-xl border ${accent ? 'border-[#00F0FF]/30 bg-[#00F0FF]/[0.04]' : 'border-white/10 bg-neutral-900'} p-4`}
    data-testid={testId}
  >
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-neutral-400 mb-1.5 font-bold">
      <span className={accent ? 'text-[#00F0FF]' : 'text-neutral-500'}>{icon}</span>
      {label}
    </div>
    <div className={`text-2xl font-bold ${accent ? 'text-[#00F0FF]' : 'text-white'}`}>{value}</div>
  </div>
);

const EmptyHint = ({ title, body }) => (
  <div className="rounded-xl border border-dashed border-white/10 bg-white/[0.02] p-8 text-center">
    <h4 className="text-sm font-bold text-white mb-1">{title}</h4>
    <p className="text-xs text-neutral-400 max-w-md mx-auto">{body}</p>
  </div>
);
